// Pure functions shared by the server (authority) and the client (readouts). No state, no side effects.
import { O2_CRITICAL, COMMS_BY_ALLOC, COOLING_DRAIN_PER_MISSING, POWER_DEMAND, POWER_OUTPUT_FULL, POWER_OUTPUT_MIN, RELAY_DAMAGED_FACTOR, HULL_LETTERS, COMMS_BASE, COMMS_HOP_FACTOR, COMMS_LOW_RESERVE_FACTOR, COMMS_MIN, COMMS_TRANSIT_FACTOR, COMMS_TRIPPED_FACTOR, DRAIN_PER_TICK, RESERVE_LOW, TICK_HZ } from "./constants.js";

export const netDrainPerTick = (multiplier: number): number => DRAIN_PER_TICK * multiplier;
export const netDrainPerSecond = (multiplier: number): number => netDrainPerTick(multiplier) * TICK_HZ;

/** Ticks until the reserve hits zero at the current drain (0 when already empty). */
export const scramEtaTicks = (reserve: number, multiplier: number): number =>
  reserve <= 0 ? 0 : Math.ceil(reserve / netDrainPerTick(multiplier));

/** "mm:ss" from a tick count (floors to whole seconds). */
export function ticksToClock(ticks: number): string {
  const s = Math.max(0, Math.floor(ticks / TICK_HZ));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/** 0..1 progress of a transit, clamped. */
export function transitProgress(tick: number, departTick: number, arriveTick: number): number {
  const span = arriveTick - departTick;
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (tick - departTick) / span));
}

/** Reactor output in power units at a given reserve. */
export const powerOutput = (reserve: number): number =>
  Math.round(POWER_OUTPUT_MIN + (POWER_OUTPUT_FULL - POWER_OUTPUT_MIN) * Math.max(0, Math.min(1, reserve / 100)));

/** Drain multiplier contribution from under-cooling. */
export const coolingMultiplier = (cooling: number): number => 1 + Math.max(0, POWER_DEMAND - cooling) * COOLING_DRAIN_PER_MISSING;

/** Server-side link quality for one crew member (before per-receiver distance). */
export function commsQualityFor(opts: { inTransit: boolean; breakerTripped: boolean; reserve: number; running: boolean; commsAlloc?: number; relayDamaged?: boolean }): number {
  if (!opts.running) return 1;
  let q = opts.commsAlloc === undefined ? COMMS_BASE : (COMMS_BY_ALLOC[Math.max(0, Math.min(4, opts.commsAlloc))] ?? COMMS_BASE);
  if (opts.relayDamaged) q *= RELAY_DAMAGED_FACTOR;
  if (opts.inTransit) q *= COMMS_TRANSIT_FACTOR;
  if (opts.breakerTripped) q *= COMMS_TRIPPED_FACTOR;
  if (opts.reserve <= RESERVE_LOW) q *= COMMS_LOW_RESERVE_FACTOR;
  return Math.min(1, Math.max(COMMS_MIN, q));
}

/** What the receiver actually gets: the speaker's link quality attenuated by deck distance. */
export const receivedQuality = (speakerQuality: number, hopsBetween: number): number =>
  Math.max(COMMS_MIN, speakerQuality * Math.pow(COMMS_HOP_FACTOR, Math.min(hopsBetween, 6)));

/** Uppercase, keep letters/digits only, and format as ABCD-1234 (partial input stays partial). */
export function normalizeHullCode(raw: string): string {
  const letters = raw.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
  const digits = raw.replace(/[^0-9]/g, "").slice(0, 4);
  return letters.length < 4 ? letters : `${letters}-${digits}`;
}
export const isHullCode = (s: string): boolean => new RegExp(`^[${HULL_LETTERS}]{4}-[0-9]{4}$`).test(s);

/** Human-readable drain itemization (the bill). Inputs mirror the server's drainMultiplier composition. */
export function drainBill(opts: { cooling: number; breakerTripped: boolean; coolantLeak: boolean }): { label: string; factor: string }[] {
  const rows: { label: string; factor: string }[] = [{ label: "BASE LOAD", factor: `−${(DRAIN_PER_TICK * TICK_HZ).toFixed(2)} %/s` }];
  const missing = Math.max(0, POWER_DEMAND - opts.cooling);
  if (missing > 0) rows.push({ label: `UNDER-COOLING (−${missing})`, factor: `×${coolingMultiplier(opts.cooling).toFixed(2)}` });
  if (opts.breakerTripped) rows.push({ label: "BREAKER FAULT", factor: "×2.5" });
  if (opts.coolantLeak) rows.push({ label: "COOLANT LEAK", factor: "×1.4" });
  return rows;
}

/**
 * Terminal signal 0..1 — how well the ship's own screens are holding up. Presentation derived from
 * synced state (like the advisory line), not a schema field. Drives the CRT glass: scanline weight,
 * grain, sync roll, dropouts, glow bleed. The screen degrades as the ship degrades, so "poor
 * reception" reads as the ship talking rather than as a filter.
 */
export function terminalSignal(opts: { reserve: number; breakerTripped: boolean; coolantLeak: boolean; o2: number; running: boolean }): number {
  if (!opts.running) return 0.92;
  let q = 1;
  q -= Math.max(0, (RESERVE_LOW - opts.reserve) / RESERVE_LOW) * 0.5; // browns out as the reserve dies
  if (opts.breakerTripped) q -= 0.2;
  if (opts.coolantLeak) q -= 0.1;
  if (opts.o2 <= O2_CRITICAL) q -= 0.15;
  return Math.max(0.15, Math.min(1, q));
}
